import React, { useEffect, useState, useRef } from 'react';
export function ServicesTimeline() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const [animationInitialized, setAnimationInitialized] = useState(false);
  const services = [{
    title: 'Build',
    description: 'A new agency built on 15 years of ecommerce experience. We design and develop stores - migrations, redesigns, new launches - with the knowledge that only comes from hundreds of builds.',
    tags: ['Design', 'Development', 'Migrations', 'Integrations', 'App Setup']
  }, {
    title: 'Email',
    description: 'Our team has set up and migrated hundreds of Klaviyo accounts. We build flows that bring customers back - not just templates, but strategy that fits how your customers actually buy.',
    tags: ['Setup', 'Migration', 'Flow Strategy']
  }, {
    title: 'Support',
    description: "Long-term partnerships, not handoffs. We stay with you after launch - updates, fixes, advice, improvements. When something needs doing, we're already across your store.",
    tags: ['Retainer', 'Updates', 'Advice']
  }];
  // Function to initialize animations
  const initializeAnimation = () => {
    if (!window.gsap || !window.ScrollTrigger || animationInitialized) return;
    const {
      gsap,
      ScrollTrigger
    } = window;
    try {
      gsap.registerPlugin(ScrollTrigger);
      const section = sectionRef.current;
      const cards = cardsRef.current;
      if (!section || !cards.length) {
        console.warn('Service cards elements not found');
        return;
      }
      // Set initial states
      gsap.set(cards, {
        y: 30,
        opacity: 0
      });
      // Animate cards
      gsap.to(cards, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 75%',
          toggleActions: 'play none none reverse',
          markers: false
        }
      });
      // Animate service tags
      cards.forEach(card => {
        const tags = card.querySelectorAll('.service-tag');
        gsap.set(tags, {
          opacity: 0,
          y: 10
        });
        ScrollTrigger.create({
          trigger: card,
          start: 'top 80%',
          onEnter: () => {
            gsap.to(tags, {
              opacity: 1,
              y: 0,
              duration: 0.3,
              stagger: 0.08,
              ease: 'power2.out'
            });
          },
          markers: false
        });
      });
      setAnimationInitialized(true);
      console.log('Service cards animations initialized');
    } catch (error) {
      console.error('Error initializing animations:', error);
    }
  };
  // Check for GSAP periodically until it's available
  useEffect(() => {
    if (window.gsap && window.ScrollTrigger) {
      initializeAnimation();
      return;
    }
    const checkInterval = setInterval(() => {
      if (window.gsap && window.ScrollTrigger) {
        initializeAnimation();
        clearInterval(checkInterval);
      }
    }, 100);
    return () => {
      clearInterval(checkInterval);
      if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      }
    };
  }, []);
  return <>
      <section ref={sectionRef} className="pt-24 pb-20 bg-[#FAF7F2]" data-id="element-173">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-18" data-id="element-174">
          {/* Three Horizontal Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-175">
            {services.map((service, index) => <div key={index} ref={el => cardsRef.current[index] = el} className="bg-white rounded-xl p-8 transition-all duration-300 hover:shadow-xl relative group" data-id="element-176">
                <h3 className="text-[28px] font-medium text-[#2C2416] mb-4 font-['Inter']" data-id="element-177">
                  {service.title}
                </h3>
                <p className="text-[#8B7355] leading-relaxed mb-6 font-['Inter']" data-id="element-178">
                  {service.description}
                </p>
                {/* Service Tags */}
                <div className="flex flex-wrap gap-2" data-id="element-179">
                  {service.tags.map((tag, idx) => <span key={idx} className="service-tag px-4 py-2 bg-[#FAF7F2] rounded-full text-sm font-medium text-[#2C2416] transition-all duration-300 hover:bg-[#D4A574] hover:text-[#FAF7F2] font-['Inter']" data-id="element-180">
                      {tag}
                    </span>)}
                </div>
                {/* Hover Accent */}
                <div className="absolute bottom-0 left-8 right-8 h-[3px] bg-[#D4A574] rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" data-id="element-181" />
              </div>)}
          </div>
        </div>
      </section>
      {/* CPG Consulting Section */}
      <section className="bg-white py-20 px-10" data-id="element-182">
        <div className="max-w-[1400px] mx-auto" data-id="element-183">
          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[60px] mb-[50px]" data-id="element-184">
            {/* Content */}
            <div data-id="element-185">
              <h2 className="text-[clamp(36px,6vw,48px)] font-normal leading-[1.1] tracking-[-2px] mb-5 font-['Inter']" data-id="element-186">
                Design & Development Partner for{' '}
                <span className="text-[#D4A574]" data-id="element-187">Wine Ecommerce</span>
              </h2>
              <p className="text-lg text-[#8B7355] leading-[1.7] mb-[15px] font-['Inter']" data-id="element-188">
                Our founders have 15+ years building winery websites for
                hundreds of wineries. We work with your chosen platform to
                deliver custom design and complete technical implementation that
                drives wine club signups and DTC revenue.
              </p>
              <div className="flex gap-[15px] flex-wrap mt-[30px]" data-id="element-189">
                <span className="service-tag px-5 py-[10px] bg-[#FAF7F2] rounded-full text-sm font-medium text-[#2C2416] transition-all duration-300 hover:bg-[#D4A574] hover:text-[#FAF7F2] font-['Inter']" data-id="element-190">
                  Custom Design
                </span>
                <span className="service-tag px-5 py-[10px] bg-[#FAF7F2] rounded-full text-sm font-medium text-[#2C2416] transition-all duration-300 hover:bg-[#D4A574] hover:text-[#FAF7F2] font-['Inter']" data-id="element-191">
                  Technical Implementation
                </span>
                <span className="service-tag px-5 py-[10px] bg-[#FAF7F2] rounded-full text-sm font-medium text-[#2C2416] transition-all duration-300 hover:bg-[#D4A574] hover:text-[#FAF7F2] font-['Inter']" data-id="element-192">
                  Wine Club Setup
                </span>
                <span className="service-tag px-5 py-[10px] bg-[#FAF7F2] rounded-full text-sm font-medium text-[#2C2416] transition-all duration-300 hover:bg-[#D4A574] hover:text-[#FAF7F2] font-['Inter']" data-id="element-193">
                  Email Marketing
                </span>
              </div>
            </div>
            {/* Image */}
            <div className="h-[400px] rounded-xl overflow-hidden" data-id="element-194">
              <img src="https://assets.modern-commerce.io/the%20lane%20image%20for%20home%20page.jpg" alt="Wine Ecommerce" className="w-full h-full object-cover rounded-xl" data-id="element-195" />
            </div>
          </div>
          {/* Centred CTA */}
          <div className="text-center" data-id="element-196">
            <a href="/wine" className="inline-block px-9 py-[18px] bg-[#2C2416] text-[#FAF7F2] rounded-full font-medium text-[15px] tracking-wide transition-all duration-300 hover:bg-[#D4A574] hover:scale-105 font-['Inter']" data-id="element-197">
              Learn More{' '}
            </a>
          </div>
        </div>
      </section>
    </>;
}